import { ClampedText } from '../../../common/ClampedText';
import { Percentage } from '../../../common/Percentage';
import { DataTableVariant } from '../../../common/table/TableTypes';
import { dataTableVariants } from '../../../common/table/dataTableVariants';
import { ConfigurationToName } from '../../dependencies/types';
import { InitialsAvatar } from '../../shared/InitialsAvatar';
import { colors, Tag } from '@sweep-io/sweep-design';
import { humanizeDate } from '../../../helpers/humanizeDate';
import { ReactNode } from 'react';
import {
  SalesforceItem,
  hasFieldProperties,
  isSweepElementConfigurationItem,
} from '../../../parser/ConfigurationItemTypes';
import Stack from '@mui/material/Stack';
import { ObjectTypeLabelAndKeyTag } from '../../../common/ObjectTypeLabelAndKey';
import isNil from 'lodash/isNil';
import { TableTypography } from '../../../common/table/utils';
import { SimpleTextCopyButton } from './SimpleTextCopyButton';
import { FunnelFieldLabels } from '../../../../constants/deprecated_fieldsManagementConsts';
import { isItemActive } from '../../helpers';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';

// Type for all possible keys across all SalesforceItem types
type ConfigurationItemKey =
  | keyof SalesforceItem
  | 'usage'
  | 'fieldType'
  | 'isRollup'
  | 'actions'
  | 'buttons'
  | 'relatedLists';

const tableTypographyVariant = dataTableVariants[DataTableVariant.medium].fontVariant;

const getColor = (isInactive: boolean) =>
  isInactive ? `${colors.grey[500]} !important` : 'inherit';

const safeDecodeURI = (value: string): string => {
  try {
    return decodeURI(value);
  } catch (error) {
    return value;
  }
};

const renderRowCell = (
  id: string,
  key: ConfigurationItemKey,
  row: SalesforceItem,
  isManagePackageInstalled: boolean,
  isInactive: boolean,
  isRollup: boolean,
) => {
  const value = row[key as keyof SalesforceItem];
  const _typographyVariant = tableTypographyVariant;
  const cellKey = `${id}-${key}-${value}`;

  switch (key) {
    case 'createdAt':
    case 'updatedAt':
      return (
        <ClampedText
          key={cellKey}
          text={
            value
              ? humanizeDate({
                  dateOrTimestamp: value as string,
                })
              : '-'
          }
          typographyVariant={_typographyVariant}
          textColor={getColor(isInactive)}
        />
      );

    case 'usage':
      if (hasFieldProperties(row)) {
        return (
          <>
            {!isNil(row.usage) || !isManagePackageInstalled ? (
              <Percentage
                key={cellKey}
                preCalculated={row.usage as number}
                active={isManagePackageInstalled}
                typographyVariant="caption"
              />
            ) : (
              <CenteredCircularProgress size={16} />
            )}
          </>
        );
      }
      return (
        <ClampedText
          key={cellKey}
          text="-"
          typographyVariant={_typographyVariant}
          textColor={getColor(isInactive)}
        />
      );

    case 'fieldType':
    case 'parentType':
      return (
        <ClampedText
          key={cellKey}
          text={
            ConfigurationToName[value as keyof typeof ConfigurationToName]?.replace('Apex ', '') ??
            FunnelFieldLabels[value as SweepFieldTypes] ??
            value
          }
          typographyVariant={_typographyVariant}
          textColor={getColor(isInactive)}
        />
      );

    case 'objectApiNames':
    case 'relatedObjects':
      const objectNames = typeof value === 'string' ? (value ? [value] : []) : (value as string[]);

      return (
        <Stack key={cellKey} gap={0.5} direction="row" flexWrap="wrap" width="200px" minWidth={0}>
          {objectNames?.map((objectName) => {
            return <ObjectTypeLabelAndKeyTag key={objectName} objectType={objectName} />;
          })}
        </Stack>
      );

    case 'name':
      return (
        <Stack
          key={cellKey}
          direction="row"
          gap={0.5}
          justifyContent="space-between"
          alignItems="flex-end"
          width="100%"
          sx={{
            '& .copy-to-clipboard-button': { visibility: 'hidden' },
            '&:hover .copy-to-clipboard-button': { visibility: 'visible' },
          }}
        >
          <ClampedText
            key={cellKey}
            text={typeof value === 'string' ? safeDecodeURI(value) : (value as ReactNode)}
            typographyVariant={_typographyVariant}
            textColor={getColor(isInactive)}
          />

          {value && typeof value === 'string' && (
            <SimpleTextCopyButton txt={safeDecodeURI(value)} itemName="API Name" />
          )}
        </Stack>
      );

    case 'actions':
      if (isSweepElementConfigurationItem(row) && row.actions) {
        return (
          <Stack
            key={cellKey}
            direction="column"
            sx={{ '& .MuiTypography-root': { color: getColor(isInactive) } }}
          >
            {row.actions.map((val) => {
              return (
                <TableTypography
                  textColor={getColor(isInactive)}
                  key={val}
                  variant={DataTableVariant.medium}
                >
                  {val}
                </TableTypography>
              );
            }) ?? '-'}
          </Stack>
        );
      }
      return (
        <ClampedText
          key={cellKey}
          text="-"
          typographyVariant={_typographyVariant}
          textColor={getColor(isInactive)}
        />
      );

    case 'label':
      return (
        <Stack key={cellKey} direction="row" gap={0.5} alignItems="center">
          <ClampedText
            text={(value ?? '-') as ReactNode}
            typographyVariant={_typographyVariant}
            textColor={getColor(isInactive)}
          />
          {isRollup && <Tag label="Rollup" color={colors.blue[100]} />}
        </Stack>
      );

    case 'buttons':
    case 'relatedLists':
      return <></>;

    default:
      return (
        <ClampedText
          key={cellKey}
          text={(value ?? '-') as ReactNode}
          typographyVariant={_typographyVariant}
          textColor={getColor(isInactive)}
        />
      );
  }
};

const createStatusCell = (row: SalesforceItem) => {
  const color = getColor(!row.isActive);
  return (
    <ClampedText
      key={`${row.id}-status`}
      text={row.isActive ? 'Active' : 'Inactive'}
      typographyVariant={tableTypographyVariant}
      textColor={color}
    />
  );
};

const createAvatarCell = (
  userIdOrName: string | undefined,
  changedAt: string | undefined,
  isInactive: boolean,
  suffix: string,
) => (
  <InitialsAvatar
    key={`${userIdOrName || 'unknown'}-${changedAt || ''}-${suffix}`}
    isInactive={isInactive}
    showUserFullName
    userIdOrName={userIdOrName || ''}
    changedAt={changedAt || ''}
  />
);

export const renderRows = ({
  items,
  activeItemId,
  isManagePackageInstalled,
}: {
  items: SalesforceItem[];
  activeItemId: string;
  isManagePackageInstalled: boolean;
}) => {
  return items.map((row) => {
    const isInactive = !isItemActive(row);
    const isActive = activeItemId === row.id;
    const isRollup = hasFieldProperties(row) ? !!row.isRollup : false;
    const usage = hasFieldProperties(row) ? row.usage : undefined;

    const rowData = Object.assign(
      {},
      ...Object.keys({ ...row, usage }).map((key) => ({
        [key]: renderRowCell(
          row.id,
          key as ConfigurationItemKey,
          row,
          !!isManagePackageInstalled,
          isInactive,
          isRollup,
        ),
      })),
    );

    return {
      ...rowData,
      status: createStatusCell(row),
      _sfApiName: row.name,
      _parentType: row.parentType,
      _objectName: row.objectApiNames?.[0],
      id: row.id,
      isRowActive: isActive,
      isClickable: true,
      isRollup,
      createdBy: createAvatarCell(row.createdBy, row.createdAt, isInactive, 'created'),
      updatedBy: createAvatarCell(row.updatedBy, row.updatedAt, isInactive, 'updated'),
    };
  });
};
